import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { getWalletHistory } from "../services/selfService";

interface WalletFilters {
  startDate: string;
  endDate: string;
  paymentMethod?: number;
  transactionStatus?: number;
}

interface WalletFilterState {
  transactionStatus: number | null;
  startDate: string;
  paymentMethod: number | null;
  endDate: string;
}

const DEFAULT_WALLET_FILTERS: WalletFilterState = {
  transactionStatus: null,
  paymentMethod: null,
  startDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
};

interface UseWalletDataProps {
  autoFetch?: boolean;
  pageSize?: number;
  enableFilters?: boolean;
}

export const useWalletData = ({ 
  autoFetch = true, 
  pageSize = 10,
  enableFilters = true 
}: UseWalletDataProps = {}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [filters, setFilters] = useState<WalletFilterState>(DEFAULT_WALLET_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<WalletFilterState>(DEFAULT_WALLET_FILTERS);
  
  const hasActiveFilters = useCallback(() => {
    if (!enableFilters) return false;
    
    return (
      filters.paymentMethod !== null ||
      filters.transactionStatus !== null ||
      filters.startDate !== DEFAULT_WALLET_FILTERS.startDate ||
      filters.endDate !== DEFAULT_WALLET_FILTERS.endDate
    );
  }, [filters, enableFilters]);

  const buildPayload = useCallback((page: number = currentPage) => {
    const from = page;
    
    const parameter: WalletFilters = {
      startDate: moment(filters.startDate, 'YYYY-MM-DD').startOf('day').toISOString(),
      endDate: moment(filters.endDate, 'YYYY-MM-DD').endOf('day').toISOString(),
    };
    if(enableFilters){

      if ( filters.transactionStatus !== null && filters.transactionStatus >= 0) {
        parameter.transactionStatus = filters.transactionStatus;
      }

      if (filters.paymentMethod !== null && filters.paymentMethod >= 0) {
        parameter.paymentMethod = filters.paymentMethod;
      }
    }


    return {
      pageSize,
      from,
      parameter,
    };
  }, [filters, currentPage, pageSize, enableFilters]);

  const fetchData = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildPayload(page);
      const response = await  getWalletHistory(payload);

      if (response?.data?.isSuccessful && response?.data?.data) {
        setData(response?.data?.data?.walletReports);
        setTotalCount(response.data.totalCount);
        setCurrentPage(page);
      } else {
        setError(response.data.message);
        toast.error(response.data.message);
      }
    } catch (err: any) {
      const errorMessage = err.data?.message || err.message || "Failed to fetch wallet transactions";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [buildPayload]);

  const updateFilters = useCallback((newFilters: Partial<WalletFilterState>) => {
    if (!enableFilters) return;
    
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, [enableFilters]);

  const updatePendingFilters = useCallback((newFilters: Partial<WalletFilterState>) => {
    if (!enableFilters) return;
    
    setPendingFilters((prev) => ({ ...prev, ...newFilters }));
  }, [enableFilters]);

  const applyFilters = useCallback(() => {
    if (!enableFilters) return;
    
    setFilters(pendingFilters);
    setCurrentPage(1);
  }, [pendingFilters, enableFilters]);

  const resetFilters = useCallback(() => {
    if (!enableFilters) return;
    
    setFilters(DEFAULT_WALLET_FILTERS);
    setPendingFilters(DEFAULT_WALLET_FILTERS);
    setCurrentPage(1);
  }, [enableFilters]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchData(currentPage);
    }
  }, [filters, currentPage, autoFetch, fetchData]);

  return {
    data,
    loading,
    error,
    totalCount,
    currentPage,
    pageSize,
    filters,
    pendingFilters,
    hasActiveFilters: hasActiveFilters(),
    fetchData,
    updateFilters,
    updatePendingFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
    refetch: () => fetchData(currentPage),
  };
};